import { useQuery } from "@tanstack/react-query";
import { format, isValid } from "date-fns";

import { createAxiosInstance } from "api/axiosConfig";

import { diagnosisItems } from "../constants/diagnosisItems";

const formatDateSafe = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return isValid(date) ? format(date, "dd-MM-yyyy") : "";
};

export const transformPPDataFromAPI = (apiData) => {
  const clinicalData = apiData.clinicalData;
  const confirmedOutcome = apiData.clinicalData.ppOutcome;

  const data = {
    ethnicityCode: clinicalData.patientDemographics?.ethnicityCode,
    registeredDate: new Date(apiData.reviewDate),
    reviewingDate: new Date(apiData.reviewDate),
    reviewingDoctor: apiData.doctorName,
    doctorId: apiData.doctorId ?? null,
    createdBy: apiData.doctorName,
    reviewDate: apiData.reviewDate ?? null,
    consultationDate:
      formatDateSafe(apiData.reviewDate) || format(new Date(), "dd-MM-yyyy"),
    eircode: clinicalData.patientDemographics?.eircode ?? "",
    diagnosis: clinicalData.clinicalDetails.registrationReasons
      .map((item) => diagnosisItems.find(({ id }) => id === item)?.name)
      .filter(Boolean)
      .join(", "),
    indications: clinicalData.registrationReasons,
    medications: [],
    date: new Date(apiData.reviewDate),
    reviewType:
      apiData.reviewType === "PP_REGISTRATION"
        ? "PP Registration"
        : apiData.reviewType,

    status: apiData.status ?? apiData.clinicalMessageStatus,
    statusErrors: apiData.statusErrors ?? [],
    statusWarnings: apiData.statusWarnings ?? [],
    clinicalMessageStatus: apiData.clinicalMessageStatus ?? null,
    clinicalMessageControlId: apiData.clinicalMessageControlId ?? null,
    clinicalMessageXml: apiData.clinicalMessageXml ?? null,
    reimbursementMessageStatus: apiData.reimbursementMessageStatus ?? null,
    reimbursementMessageControlId: apiData.reimbursementMessageControlId ?? null,

    registrationReasons: clinicalData.clinicalDetails.registrationReasons ?? [],
    yearOfRegistration:
      clinicalData.clinicalDetails.yearOfRegistration?.toString(),
    additionalRiskFactors:
      clinicalData.clinicalDetails.additionalRiskFactors ?? [],
    yearOfAdditionalRiskFactor:
      clinicalData.clinicalDetails.yearOfAdditionalRiskFactor ?? {},
    medicationReview: clinicalData.medicationReview ? "yes" : "no",

    smokingStatus: clinicalData.riskFactors?.smokingStatus,
    vapingStatus: clinicalData.riskFactors?.vapingStatus,
    weight: clinicalData.riskFactors?.weightKg?.toString(),
    height: clinicalData.riskFactors?.heightCm?.toString(),
    bmi: clinicalData.riskFactors?.bmi?.toString(),
    waistCircumference:
      clinicalData.riskFactors?.waistCircumferenceCm?.toString(),
    qRisk3Score: clinicalData.riskFactors?.qrisk3Score?.toString(),
    alcoholRiskScore: clinicalData.riskFactors?.alcoholAuditCScore?.toString(),
    alcoholFullAuditScore: clinicalData.riskFactors?.alcoholFullAuditScore?.toString(),
    physicalActivityQ1: clinicalData.riskFactors?.physicalActivityQ1,
    physicalActivityQ2: clinicalData.riskFactors?.physicalActivityQ2,
    smokingInterventions: clinicalData.riskFactors?.smokingInterventions ?? [],
    alcoholDependenceInterventions:
      clinicalData.riskFactors?.alcoholDependenceInterventions ?? [],
    alcoholHigherRiskInterventions:
      clinicalData.riskFactors?.alcoholHigherRiskInterventions ?? [],
    alcoholIncreasingRiskInterventions:
      clinicalData.riskFactors?.alcoholIncreasingRiskInterventions ?? [],
    weightHighRiskInterventions:
      clinicalData.riskFactors?.weightHighRiskInterventions ?? [],
    weightIncreasedRiskInterventions:
      clinicalData.riskFactors?.weightIncreasedRiskInterventions ?? [],
    weightNormalInterventions:
      clinicalData.riskFactors?.weightNormalInterventions ?? [],
    physicalActivityInterventions:
      clinicalData.riskFactors?.physicalActivityInterventions ?? [],

    influenzaVaccine: clinicalData.riskFactors?.influenzaVaccine,
    influenzaVaccineDate: formatDateSafe(
      clinicalData.riskFactors?.influenzaVaccineDate
    ),
    pneumococcalVaccine: clinicalData.riskFactors?.pneumococcalVaccine,
    pneumococcalVaccineDate: formatDateSafe(
      clinicalData.riskFactors?.pneumococcalVaccineDate
    ),
    covidVaccine: clinicalData.riskFactors?.covidVaccine,
    covidVaccineStatusUpToDate: clinicalData.riskFactors
      ?.covidVaccineStatusUpToDate
      ? "YES"
      : "NO",

    pulse: clinicalData.physicalExam?.pulseRate?.toString(),
    pulseRhythm: clinicalData.physicalExam?.pulseRhythm,
    bp1: clinicalData.physicalExam?.systolicBloodPressure?.toString(),
    bp2: clinicalData.physicalExam?.diastolicBloodPressure?.toString(),

    ecgSinceLastReview: clinicalData.diagnosticInvestigations
      ?.ecgSinceLastReview
      ? "YES"
      : "NO",
    ecgResult: clinicalData.diagnosticInvestigations?.ecgResult,
    lastEcgDate: clinicalData.diagnosticInvestigations?.lastEcgDate?.toString(),
    echoSinceLastReview:
      clinicalData.diagnosticInvestigations?.echoSinceLastReview === true
        ? "YES"
        : clinicalData.diagnosticInvestigations?.echoSinceLastReview === false
        ? "NO"
        : clinicalData.diagnosticInvestigations?.echoSinceLastReview === null
        ? "NOT_AVAILABLE"
        : "",
    echoResult: clinicalData.diagnosticInvestigations?.echoResult,
    lastEchoDate: clinicalData.diagnosticInvestigations?.lastEchoDate?.toString(),

    haemoglobin: clinicalData.laboratoryInvestigations?.haemoglobin?.toString(),
    totalCholesterol:
      clinicalData.laboratoryInvestigations?.totalCholesterol?.toString(),
    hdlCholesterol:
      clinicalData.laboratoryInvestigations?.hdlCholesterol?.toString(),
    nonHdlCholesterol:
      clinicalData.laboratoryInvestigations?.nonHdlCholesterol?.toString(),
    ldlCholesterol: clinicalData.laboratoryInvestigations?.ldlCholesterol,
    triglycerides:
      clinicalData.laboratoryInvestigations?.triglycerides?.toString(),
    hba1c: clinicalData.laboratoryInvestigations?.hba1c?.toString(),
    serumCreatinine:
      clinicalData.laboratoryInvestigations?.serumCreatinine?.toString(),
    egfr: clinicalData.laboratoryInvestigations?.egfr,
    fastingGlucose:
      clinicalData.laboratoryInvestigations?.fastingGlucose === "NA"
        ? "NA"
        : clinicalData.laboratoryInvestigations?.fastingGlucose?.toString(),
    acr:
      clinicalData.laboratoryInvestigations?.acr === "NA"
        ? "NA"
        : clinicalData.laboratoryInvestigations?.acr?.toString(),
    ntProBNP:
      clinicalData.laboratoryInvestigations?.ntProBNP === "NA"
        ? "NA"
        : clinicalData.laboratoryInvestigations?.ntProBNP?.toString(),
    tftConducted: clinicalData.laboratoryInvestigations?.tftConducted
      ? "YES"
      : "NO",
    lftConducted: clinicalData.laboratoryInvestigations?.lftConducted
      ? "YES"
      : "NO",

    educationProvided: clinicalData.patientEducation?.educationProvided,
    referredToEducation: clinicalData.patientEducation?.referredToEducation,
    referredToDiabetesPrevention:
      clinicalData.patientEducation?.referredToDiabetesPrevention,
    referralDetails: clinicalData.patientEducation?.referralDetails,
    agreedWrittenCarePlan: clinicalData.patientEducation?.agreedWrittenCarePlan,

    carePlan: clinicalData.carePlan,

    diagnosedWithChronicDisease: confirmedOutcome?.diagnosedWithChronicDisease
      ? "YES"
      : "NO",
    // Raw CDM diagnosis codes — used to prefill a fresh CDM registration from a
    // PP outcome of "diagnosed with chronic disease" (mapped by id, KIN-1162).
    cdmDiagnosisCodes: confirmedOutcome?.cdmDiagnoses ?? [],
    // Per-disease diagnosis years carried into a transferred CDM registration
    // (PP -> CDM). BE contract (KIN-1406): ppOutcome must emit this map keyed by
    // the same CDM disease code enums. Empty until BE ships the field -> the
    // transferred years stay on the current-year default.
    cdmDiagnosisYears: confirmedOutcome?.cdmDiagnosisYears ?? {},
    diabetesType2:
      confirmedOutcome?.cdmDiagnoses?.includes("DIABETES_TYPE_2") || false,
    asthma: confirmedOutcome?.cdmDiagnoses?.includes("ASTHMA") || false,
    copd: confirmedOutcome?.cdmDiagnoses?.includes("COPD") || false,
    heartFailure:
      confirmedOutcome?.cdmDiagnoses?.includes("HEART_FAILURE") || false,
    atrialFibrillation:
      confirmedOutcome?.cdmDiagnoses?.includes("ATRIAL_FIBRILLATION") || false,
    ischaemicHeartDisease:
      confirmedOutcome?.cdmDiagnoses?.includes("ISCHAEMIC_HEART_DISEASE") ||
      false,
    stroke: confirmedOutcome?.cdmDiagnoses?.includes("STROKE") || false,
    tia: confirmedOutcome?.cdmDiagnoses?.includes("TIA") || false,
  };

  return data;
};

export const useGetPP = (ppId) => {

  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["ppItem", ppId, tenantVal],
    queryFn: async () => {
      if (!ppId) return null;
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/cdm/reviews/${ppId}`
      );

      return transformPPDataFromAPI(response.data);
    },
    enabled: !!ppId,
    meta: { errorMessage: "Error fetching PP" },
  });
};
